import { useLanguage } from '../i18n/LanguageContext';
import { createContext, useState, useMemo, useContext } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';
import { getDesignTokens, AppThemeMode } from '../theme';

interface ColorModeContextType {
    mode: AppThemeMode;
    setThemeMode: (mode: AppThemeMode) => void;
    toggleColorMode: () => void;
}

export const ColorModeContext = createContext<ColorModeContextType>({
    mode: 'midnight',
    setThemeMode: () => {},
    toggleColorMode: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeProvider = ({ children }: { children: React.ReactNode }) => {
    const { direction } = useLanguage();
    const [mode, setMode] = useState<AppThemeMode>(() => {
        const savedMode = localStorage.getItem('colorMode');
        return (savedMode === 'light' || savedMode === 'dark' || savedMode === 'midnight') ? savedMode as AppThemeMode : 'midnight';
    });

    const colorMode = useMemo(
        () => ({
            mode,
            setThemeMode: (newMode: AppThemeMode) => {
                localStorage.setItem('colorMode', newMode);
                setMode(newMode);
            },
            toggleColorMode: () => {
                setMode((prevMode) => {
                    const newMode = prevMode === 'light' ? 'midnight' : prevMode === 'midnight' ? 'dark' : 'light';
                    localStorage.setItem('colorMode', newMode);
                    return newMode;
                });
            },
        }),
        [mode],
    );

    const theme = useMemo(() => createTheme({ ...getDesignTokens(mode), direction, ...(direction === 'rtl' ? { typography: { ...getDesignTokens(mode).typography, fontFamily: 'Tahoma, Arial, sans-serif' } } : {}) } as any), [mode, direction]);

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                {children}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};
